import { useState } from "react";
import { Button, useTheme } from "@mui/material";
import cn from "../../utils/cn";
import { COMPLETION_REASON_LABELS, getAIErrorMessage, submitAIReview } from "../../utils/aiInterviewApi";
import {
  BulletList,
  Chips,
  PRIORITY_LABELS,
  SECTION_LABELS,
  ScoreBar,
  Section,
  WarningList,
  byPriority,
  formatDateTime,
  formatDuration,
  humanize,
  isScore,
  toText,
} from "./aiShared";

const RECOMMENDATIONS = {
  STRONG_HIRE: { label: "Strong hire", className: "bg-green-600 text-white" },
  HIRE: { label: "Hire", className: "bg-green-100 text-green-800 border border-green-300" },
  PROCEED: { label: "Proceed", className: "bg-blue-100 text-blue-800 border border-blue-300" },
  BORDERLINE: { label: "Borderline", className: "bg-amber-100 text-amber-800 border border-amber-300" },
  DO_NOT_PROCEED: { label: "Do not proceed", className: "bg-red-100 text-red-800 border border-red-300" },
  INSUFFICIENT_EVIDENCE: { label: "Insufficient evidence", className: "bg-gray-100 text-gray-700 border border-gray-300" },
};

const SCORE_FIELDS = [
  ["job_match_score", "Job match"],
  ["technical_score", "Technical"],
  ["problem_solving_score", "Problem solving"],
  ["practical_experience_score", "Practical experience"],
  ["communication_of_technical_ideas_score", "Communicating technical ideas"],
];

const CLAIM_STATUS = {
  STRONGLY_SUPPORTED: { label: "Strongly supported", className: "text-green-800 bg-green-50 border-green-300" },
  SUPPORTED: { label: "Supported", className: "text-green-800 bg-green-50 border-green-300" },
  PARTIALLY_SUPPORTED: { label: "Partially supported", className: "text-amber-800 bg-amber-50 border-amber-300" },
  NOT_VALIDATED: { label: "Not validated", className: "text-gray-700 bg-gray-50 border-gray-300" },
  CONTRADICTED: { label: "Contradicted", className: "text-red-800 bg-red-50 border-red-300" },
};

const DECISIONS = [
  ["advance", "Advance"],
  ["reject", "Reject"],
  ["hold", "Hold"],
  ["another_round", "Another round"],
];
const DECISION_LABELS = Object.fromEntries(DECISIONS);

const DEFAULT_DISCLAIMER = "AI-generated assessment for hiring-team review. Not a hiring decision.";

// Candidate questions also say whether the AI answered them
const candidateQuestionText = (item) =>
  item && typeof item === "object" && item.answered_by_ai !== undefined
    ? `${toText(item.question)}${item.answered_by_ai ? " (answered by the AI)" : " (not answered)"}`
    : toText(item);

const EvidenceLinks = ({ ids, questionSeq, onOpenQuestion }) => {
  if (!ids?.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 ml-1">
      {ids.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onOpenQuestion(id)}
          className="rounded bg-gray-100 hover:bg-gray-200 px-1.5 text-xs text-blue-700"
          aria-label={`View question ${questionSeq[id] ?? id}`}
        >
          {questionSeq[id] !== undefined ? `Q${questionSeq[id]}` : id}
        </button>
      ))}
    </span>
  );
};

const EvidenceList = ({ items, questionSeq, onOpenQuestion }) => (
  <ul className="list-disc pl-5 text-sm flex flex-col gap-1">
    {items.map((item, index) => (
      <li key={index}>
        {toText(item)}
        <EvidenceLinks ids={item.evidence_question_ids} questionSeq={questionSeq} onOpenQuestion={onOpenQuestion} />
      </li>
    ))}
  </ul>
);

const Facts = ({ facts }) => {
  const theme = useTheme();
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {facts.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3 border-b py-1" style={{ borderColor: theme.palette.secondary.v4 }}>
          <dt style={{ color: theme.palette.text.secondary }}>{label}</dt>
          <dd className="text-right">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

const IntegritySection = ({ integrity }) => {
  if (!integrity) return null;
  const nameMatch =
    integrity.candidate_name_matched === true
      ? " (matches the candidate)"
      : integrity.candidate_name_matched === false
        ? " (doesn't match the candidate)"
        : "";
  const facts = [
    ["Joined as", integrity.candidate_display_name ? `${integrity.candidate_display_name}${nameMatch}` : ""],
    ["Camera checked", typeof integrity.camera_checked === "boolean" ? (integrity.camera_checked ? "Yes" : "No") : ""],
    [
      "Camera off",
      isScore(integrity.camera_off_seconds)
        ? `${formatDuration(integrity.camera_off_seconds)}${integrity.camera_off_events ? ` (${integrity.camera_off_events} times)` : ""}`
        : "",
    ],
    ["Most people in the call", isScore(integrity.max_participants) ? String(integrity.max_participants) : ""],
    ["Unknown participants", integrity.unknown_participants?.length ? integrity.unknown_participants.map(toText).join(", ") : ""],
  ].filter(([, value]) => value);
  const warnings = integrity.warnings || [];
  if (!facts.length && !warnings.length) return null;

  return (
    <Section title="Interview integrity">
      {facts.length > 0 && <Facts facts={facts} />}
      {warnings.length > 0 && <WarningList warnings={warnings} />}
    </Section>
  );
};

const PanelFeedback = ({ panelist, questionSeq, onOpenQuestion }) => {
  const theme = useTheme();
  const muted = { color: theme.palette.text.secondary };
  const disagreements = panelist.disagreements || [];
  const questions = panelist.questions || [];
  const questionIds = new Set(questions.map((question) => question.question_id));
  // Disagreements are question ids; anything else is shown as text
  const disagreementNotes = disagreements.filter((item) => !questionIds.has(item));
  const isDisagreement = (question) => question.disagreement || disagreements.includes(question.question_id);

  return (
    <Section title="Panel feedback">
      <Facts
        facts={[
          ["Average panel rating", isScore(panelist.average_rating) ? `${panelist.average_rating.toFixed(1)}/5` : "—"],
          ["Questions rated", String(panelist.questions_rated ?? 0)],
          ["AI agreement", isScore(panelist.ai_agreement) ? `${Math.round(panelist.ai_agreement * 100)}%` : "—"],
          ...(panelist.disclosure
            ? [["AI note-taker disclosed", `${toText(panelist.disclosure.disclosed_by)} · ${formatDateTime(panelist.disclosure.at)}`]]
            : []),
        ]}
      />
      {disagreementNotes.length > 0 && (
        <div className="p-3 rounded border border-amber-300 bg-amber-50 text-amber-900">
          <BulletList items={disagreementNotes} />
        </div>
      )}
      {questions.length > 0 && (
        <ul className="flex flex-col gap-2">
          {questions.map((question) => (
            <li
              key={question.question_id}
              className={cn(
                "rounded-lg border p-3 flex flex-col gap-1 text-sm",
                isDisagreement(question) ? "border-amber-400 bg-amber-50" : "border-gray-200"
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <span>
                  {toText(question.question)}
                  <EvidenceLinks ids={[question.question_id]} questionSeq={questionSeq} onOpenQuestion={onOpenQuestion} />
                </span>
                <span className="whitespace-nowrap text-xs font-medium">
                  AI {isScore(question.ai_score) ? `${question.ai_score.toFixed(1)}/10` : "—"}
                  {" · "}
                  Panel {isScore(question.panelist_rating) ? `${question.panelist_rating.toFixed(1)}/5` : "—"}
                </span>
              </div>
              {isDisagreement(question) && <span className="text-xs font-medium text-amber-900">AI and panel disagree</span>}
              {(question.reviews || []).map((review, index) => (
                <p key={index} className="text-xs" style={muted}>
                  {toText(review.reviewer) || "Reviewer"}
                  {isScore(review.rating) ? ` · ${review.rating}/5` : ""}
                  {review.notes ? `: ${toText(review.notes)}` : ""}
                </p>
              ))}
            </li>
          ))}
        </ul>
      )}
      {panelist.notes?.length > 0 && (
        <div>
          <p className="text-xs font-medium">Panel notes</p>
          <ul className="flex flex-col gap-1 text-sm">
            {panelist.notes.map((note, index) => (
              <li key={index}>
                {toText(note.text)}
                <span className="text-xs ml-1" style={muted}>
                  {[toText(note.by), formatDateTime(note.at)].filter(Boolean).join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
};

const ReviewForm = ({ interviewId, review, onSaved }) => {
  const theme = useTheme();
  const [decision, setDecision] = useState(review?.decision || "");
  const [agrees, setAgrees] = useState(typeof review?.agrees_with_ai === "boolean" ? String(review.agrees_with_ai) : "");
  const [notes, setNotes] = useState(review?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!decision) {
      setError("Choose a decision.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await submitAIReview(interviewId, {
        decision,
        agreesWithAi: agrees === "" ? undefined : agrees === "true",
        notes,
      });
      onSaved(saved);
    } catch (err) {
      setError(getAIErrorMessage(err, "Couldn't save the review."));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { borderColor: theme.palette.secondary.v4, color: "#111111", backgroundColor: "#ffffff" };
  const reviewed = review?.status === "reviewed";

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4" style={{ borderColor: theme.palette.secondary.v4 }}>
      {reviewed && (
        <div className="text-sm rounded bg-gray-50 px-3 py-2">
          <p>
            <span className="font-medium">{DECISION_LABELS[review.decision] || humanize(review.decision)}</span>
            {typeof review.agrees_with_ai === "boolean" ? ` · ${review.agrees_with_ai ? "Agrees" : "Disagrees"} with the AI` : ""}
          </p>
          {review.notes && <p className="whitespace-pre-line">{toText(review.notes)}</p>}
          <p className="text-xs" style={{ color: theme.palette.text.secondary }}>
            Reviewed{review.reviewed_at ? ` on ${formatDateTime(review.reviewed_at)}` : ""}. You can update the decision below.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="ai-review-decision" className="block text-sm font-medium mb-1">
            Decision <span className="text-red-500">*</span>
          </label>
          <select
            id="ai-review-decision"
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm"
            style={inputStyle}
          >
            <option value="">Select decision</option>
            {DECISIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ai-review-agrees" className="block text-sm font-medium mb-1">Agree with the AI?</label>
          <select
            id="ai-review-agrees"
            value={agrees}
            onChange={(e) => setAgrees(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm"
            style={inputStyle}
          >
            <option value="">No answer</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="ai-review-notes" className="block text-sm font-medium mb-1">Notes</label>
        <textarea
          id="ai-review-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={4000}
          rows={3}
          className="w-full px-3 py-2 border rounded-md text-sm"
          style={inputStyle}
        />
      </div>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={saving}
          sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none" }}
        >
          {saving ? "Saving..." : reviewed ? "Update review" : "Save review"}
        </Button>
      </div>
    </div>
  );
};

// The AI report (both modes). `questionSeq` maps question ids to their number for evidence links.
export default function AIReportView({ report, interviewId, questionSeq, onOpenQuestion, onReviewSaved }) {
  const theme = useTheme();
  const recommendation = RECOMMENDATIONS[report.recommendation] || { label: humanize(report.recommendation), className: "bg-gray-100" };
  const meta = report.interview_meta || {};
  const skills = Object.entries(report.skills || {}).sort(byPriority(([, info]) => info.jd_priority));
  const sectionScores = Object.entries(report.section_scores || {}).filter(([, value]) => isScore(value));
  const muted = { color: theme.palette.text.secondary || "#6b7280" };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2 max-w-xl">
          <span className={cn("self-start rounded-full px-3 py-1 text-sm font-semibold", recommendation.className)}>
            {recommendation.label}
          </span>
          {report.hiring_recommendation && <p className="text-sm">{toText(report.hiring_recommendation)}</p>}
        </div>
        <div className="text-right">
          <div className="text-4xl font-semibold leading-none">
            {isScore(report.overall_score) ? report.overall_score.toFixed(1) : "—"}
            <span className="text-base font-normal" style={muted}>/10</span>
          </div>
          <div className="text-xs mt-1" style={muted}>Overall score</div>
        </div>
      </div>

      <p className="text-xs italic rounded bg-gray-50 px-3 py-2" style={muted}>
        {toText(report.disclaimer) || DEFAULT_DISCLAIMER}
      </p>

      {report.insufficient_evidence && (
        <p className="p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm">
          The interview didn't produce enough evidence for a reliable recommendation.
        </p>
      )}

      <p className="text-xs" style={muted}>
        {[
          meta.started_at && formatDateTime(meta.started_at),
          meta.duration_seconds && `Duration ${formatDuration(meta.duration_seconds)}`,
          meta.completion_reason && (COMPLETION_REASON_LABELS[meta.completion_reason] || humanize(meta.completion_reason)),
          isScore(meta.questions_asked) && `${meta.questions_asked} questions`,
          isScore(meta.follow_ups) && `${meta.follow_ups} follow-ups`,
          isScore(report.coverage) && `Coverage ${Math.round(report.coverage * 100)}%`,
          isScore(report.scored_answers) && `${report.scored_answers} scored answers`,
        ].filter(Boolean).join(" · ")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {SCORE_FIELDS.map(([field, label]) => <ScoreBar key={field} label={label} value={report[field]} />)}
      </div>

      {report.interview_summary && (
        <Section title="Summary">
          <p className="text-sm whitespace-pre-line">{toText(report.interview_summary)}</p>
        </Section>
      )}

      {(report.strengths?.length > 0 || report.weaknesses?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {report.strengths?.length > 0 && (
            <Section title="Strengths">
              <EvidenceList items={report.strengths} questionSeq={questionSeq} onOpenQuestion={onOpenQuestion} />
            </Section>
          )}
          {report.weaknesses?.length > 0 && (
            <Section title="Weaknesses">
              <EvidenceList items={report.weaknesses} questionSeq={questionSeq} onOpenQuestion={onOpenQuestion} />
            </Section>
          )}
        </div>
      )}

      {skills.length > 0 && (
        <Section title="Skills">
          <div className="overflow-x-auto border rounded-md" style={{ borderColor: theme.palette.secondary.v4 }}>
            <table className="w-full text-sm">
              <thead className="text-left" style={{ backgroundColor: theme.palette.secondary.v5 }}>
                <tr>
                  <th className="px-3 py-2 font-medium">Skill</th>
                  <th className="px-3 py-2 font-medium">Priority</th>
                  <th className="px-3 py-2 font-medium">Score</th>
                  <th className="px-3 py-2 font-medium">Level shown</th>
                  <th className="px-3 py-2 font-medium">Questions</th>
                </tr>
              </thead>
              <tbody>
                {skills.map(([skill, info]) => (
                  <tr key={skill} className="border-t" style={{ borderColor: theme.palette.secondary.v4 }}>
                    <td className="px-3 py-2">{skill}</td>
                    <td className="px-3 py-2">{PRIORITY_LABELS[info.jd_priority] || humanize(info.jd_priority) || "—"}</td>
                    <td className="px-3 py-2">{isScore(info.score) ? `${info.score.toFixed(1)}/10` : "—"}</td>
                    <td className="px-3 py-2">{humanize(toText(info.demonstrated_level)) || "—"}</td>
                    <td className="px-3 py-2">{info.questions ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {sectionScores.length > 0 && (
        <Section title="Section scores">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {sectionScores.map(([section, value]) => (
              <ScoreBar key={section} label={SECTION_LABELS[section] || humanize(section)} value={value} />
            ))}
          </div>
        </Section>
      )}

      {report.resume_validation?.length > 0 && (
        <Section title="Resume claims">
          <ul className="flex flex-col gap-2">
            {report.resume_validation.map((claim) => {
              const status = CLAIM_STATUS[claim.status] || { label: humanize(claim.status), className: "bg-gray-50 border-gray-300" };
              return (
                <li key={claim.claim_id} className="text-sm flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn("rounded-full border px-2 py-0.5 text-xs", status.className)}>{status.label}</span>
                    <span>{toText(claim.claim)}</span>
                  </div>
                  {(claim.reason || claim.evidence_question_ids?.length > 0) && (
                    <p className="text-xs" style={muted}>
                      {toText(claim.reason)}
                      <EvidenceLinks ids={claim.evidence_question_ids} questionSeq={questionSeq} onOpenQuestion={onOpenQuestion} />
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {report.untested_areas?.length > 0 && (
        <Section title="Not covered"><Chips items={report.untested_areas} /></Section>
      )}
      {report.suggested_focus_next_round?.length > 0 && (
        <Section title="Focus for the next round"><BulletList items={report.suggested_focus_next_round} /></Section>
      )}
      {report.reviewer_attention_points?.length > 0 && (
        <Section title="Points for the reviewer">
          <div className="p-3 rounded border border-amber-300 bg-amber-50 text-amber-900">
            <BulletList items={report.reviewer_attention_points} />
          </div>
        </Section>
      )}

      <IntegritySection integrity={report.integrity} />

      {report.technical_issues?.length > 0 && (
        <Section title="Technical issues"><WarningList warnings={report.technical_issues} /></Section>
      )}

      {report.panelist && (
        <PanelFeedback panelist={report.panelist} questionSeq={questionSeq} onOpenQuestion={onOpenQuestion} />
      )}

      {report.candidate_questions?.length > 0 && (
        <Section title="What the candidate asked"><BulletList items={report.candidate_questions} format={candidateQuestionText} /></Section>
      )}
      {report.data_quality_notes?.length > 0 && (
        <Section title="Data quality notes"><BulletList items={report.data_quality_notes} /></Section>
      )}

      <Section title="Your decision">
        <ReviewForm interviewId={interviewId} review={report.review} onSaved={onReviewSaved} />
      </Section>
    </div>
  );
}
